'use client'

import * as React from 'react'

import { createContext } from '~logic/lib/react'

import { usePersistedOrderId } from '../hooks/usePersistedOrderId'

type TSetOrderId = ReturnType<typeof usePersistedOrderId>['setOrderId']
type TInitOrderOptions = FirstParam<TSetOrderId>

type TPersistedOrderIdProps = OmitSafe<Parameters<typeof usePersistedOrderId>[0], 'onOrderClear'>

type TOrderUpdate = {
  clearOrder: () => void
  initializeOrder: (options?: TInitOrderOptions) => void
  updateOrderToken: (token: TInitOrderOptions['userToken']) => void
}

type TOrderState =
  | {
      isLoadingPersistence: true
      isOrderInitialized: boolean
      orderId: null
      orderAuthToken: null
    }
  | {
      isLoadingPersistence: false
      isOrderInitialized: boolean
      orderId: Nullable<ID>
      orderAuthToken: Nullable<string>
    }

type TOrderValue = TOrderUpdate & TOrderState

const [ValueProvider, useOrderIdCtx] = createContext<TOrderValue>(`OrderValue`)
const [UpdateProvider, useSetOrderIdCtx] = createContext<TOrderUpdate>(`SetOrderId`)

type TProps = RequiredChildren &
  TPersistedOrderIdProps & {
    onOrderClear?: () => void
  }

/**
 * Global provider of the order that is current being edited.
 * Use `useOrderId` hook to access that info anywhere in the app.
 */
export const OrderProvider: React.FC<TProps> = ({
  order,
  setOrder,
  resetOrder,
  onOrderClear,
  resetPersistence,
  children,
}) => {
  const [initializeOrderCalled, setInitializeOrderCalled] = React.useState(false)

  const resetOrderInitialization = React.useCallback(() => {
    setInitializeOrderCalled(false)
  }, [])

  const resetOnOrderClear = React.useCallback(() => {
    resetOrderInitialization()
    onOrderClear?.()
  }, [onOrderClear, resetOrderInitialization])

  // remove persisted order ID value
  // enable the next attempt to create a new order ID
  const {
    orderId, //
    setOrderId,
    clearOrder,
    orderAuthToken,
    updateOrderToken,
    isLoadingPersistence,
  } = usePersistedOrderId({
    onOrderClear: resetOnOrderClear,
    order,
    setOrder,
    resetOrder,
    resetPersistence,
  })

  const initializeOrder = React.useCallback(
    (options?: TInitOrderOptions) => {
      if (options) {
        setOrderId(options)
      }

      setInitializeOrderCalled(true)
    },
    [setOrderId],
  )

  // for this to work on FormScreen & when we already have persisted orderId
  const isOrderInitialized = React.useMemo(
    () => Boolean(orderId) || initializeOrderCalled,
    [orderId, initializeOrderCalled],
  )

  const contextValue = React.useMemo(() => {
    if (isLoadingPersistence) {
      return {
        orderId: null,
        clearOrder,
        initializeOrder,
        updateOrderToken,
        isOrderInitialized,
        orderAuthToken: null,
        isLoadingPersistence,
      }
    }

    return {
      orderId,
      clearOrder,
      orderAuthToken,
      initializeOrder,
      updateOrderToken,
      isOrderInitialized,
      isLoadingPersistence,
    }
  }, [
    clearOrder,
    initializeOrder,
    isLoadingPersistence,
    isOrderInitialized,
    orderAuthToken,
    orderId,
    updateOrderToken,
  ])

  const updaters: TOrderUpdate = React.useMemo(() => {
    return { clearOrder, initializeOrder, updateOrderToken }
  }, [clearOrder, initializeOrder, updateOrderToken])

  return (
    <UpdateProvider value={updaters}>
      <ValueProvider value={contextValue}>{children}</ValueProvider>
    </UpdateProvider>
  )
}

export { useOrderIdCtx, useSetOrderIdCtx }
